// src/tabs/logements/AlentoursAdminEditor.jsx — PR-FIX-4c (ADR-161)
//
// Éditeur admin grid visuel + drag-drop pour la section JSONB
// `livret_sections_auto.content.secteurs` (catégorie 'alentours').
//
// Remplace l'édition textarea JSON brute existante (SectionAutoEditModal reste
// disponible en collapse "Édition avancée JSON" pour fallback dev urgence).
//
// Layout :
//   - Header : titre + count catégories actives + boutons Régénérer IA / Vider
//   - 7 accordéons (1 par catégorie : restaurants_bars, supermarches,
//     boulangeries, marches_locaux, visites_culture, parcs_nature, transports)
//   - Chaque accordéon : header (emoji + nom catégorie + count) + grid de
//     cards POI (AlentoursAdminCard) + bouton "+ Ajouter manuellement"
//
// Drag-drop intra-catégorie : 1 DndContext par accordéon (les catégories
// n'échangent pas de POIs entre elles). PointerSensor distance:8 +
// KeyboardSensor sortableKeyboardCoordinates (a11y WCAG 2.1 AA pattern ADR-114).
//
// isDraggingRef anti-double-commit Strict Mode + optimistic update local
// (resync depuis prop content quand !isDraggingRef.current).
//
// Calls API (consomme PR-FIX-4b backend actions) :
//   - `livret_alentours_poi_toggle` flag=manual_pinned/manual_excluded/displayed_to_guest
//   - `livret_alentours_poi_reorder` ordered_place_ids[]
//   - `livret_alentours_poi_add_manual` poi {...}
//   - `livret_alentours_poi_delete_manual` manual_id_or_place_id (PRÉCISION
//     pessimist S1 : passer `manual_id` car protection source=manual côté serveur)
//   - `livret_sections_auto_generate` action='alentours' (régénération IA)
//
// Auth : récupère JWT supabase via supabase.auth.getSession() (helper
// getAuthHeaders local, pattern LivretModulesEditor + LivretSectionsAutoEditor).

import { useState, useEffect, useCallback, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DS } from "../../lib/ui";
import { supabase } from "../../supabase";
import AlentoursAdminCard, { getPoiIdentifier } from "./AlentoursAdminCard";
import AddPoiModal from "./AddPoiModal";

// 7 catégories supportées (cf. ADR-153 + ADR-158).
const ALENTOURS_CATEGORIES = [
  { key: "restaurants_bars", icon: "🍽️", label: "Restaurants & Bars" },
  { key: "supermarches", icon: "🛒", label: "Supermarchés" },
  { key: "boulangeries", icon: "🥖", label: "Boulangeries" },
  { key: "marches_locaux", icon: "🥕", label: "Marchés locaux" },
  { key: "visites_culture", icon: "🏛️", label: "Visites & Culture" },
  { key: "parcs_nature", icon: "🌳", label: "Parcs & Nature" },
  { key: "transports", icon: "🚌", label: "Transports proches" },
];

async function getAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

async function _apiCall(action, { method = "POST", body } = {}) {
  const url = `/api/beds24-mapping?action=${action}`;
  const headers = await getAuthHeaders();
  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({ error: "parse_fail" }));
  if (!res.ok) {
    const err = new Error(json.message || json.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.error = json.error;
    throw err;
  }
  return json;
}

export default function AlentoursAdminEditor({ logement_id, row, onReload }) {
  // row = { id, section_type:'alentours', content:{secteurs:{...}}, enabled, ... }
  const initialContent = row?.content || { secteurs: {} };

  // ── State local optimiste (drag + toggles avant resync prop) ───────────
  const [localSecteurs, setLocalSecteurs] = useState(
    initialContent.secteurs || {},
  );
  const isDraggingRef = useRef(false);

  // Resync depuis prop quand row.content change ET pas en drag actif.
  useEffect(() => {
    if (!isDraggingRef.current) {
      setLocalSecteurs(row?.content?.secteurs || {});
    }
  }, [row]);

  const [busyKeys, setBusyKeys] = useState({}); // { identifier: true } durant API call
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [addModalForKey, setAddModalForKey] = useState(null);

  // Sensors @dnd-kit (pattern ADR-114).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ── Helpers business ────────────────────────────────────────────────────
  const setBusy = useCallback((identifier, val) => {
    setBusyKeys((prev) => {
      const next = { ...prev };
      if (val) next[identifier] = true;
      else delete next[identifier];
      return next;
    });
  }, []);

  const countAffichage = useCallback(() => {
    let total = 0;
    let displayed = 0;
    for (const k of Object.keys(localSecteurs)) {
      const arr = Array.isArray(localSecteurs[k]) ? localSecteurs[k] : [];
      total += arr.length;
      for (const p of arr) {
        if (p && p.displayed_to_guest !== false && p.manual_deleted !== true) {
          displayed += 1;
        }
      }
    }
    return { total, displayed };
  }, [localSecteurs]);

  const { total, displayed } = countAffichage();
  const activeCategoriesCount = ALENTOURS_CATEGORIES.filter(
    (c) => Array.isArray(localSecteurs[c.key]) && localSecteurs[c.key].length > 0,
  ).length;

  // ── API toggles ────────────────────────────────────────────────────────
  async function callToggle(poi, flag, value) {
    const identifier = getPoiIdentifier(poi);
    if (!identifier) {
      setError("POI sans identifiant — impossible de toggler");
      return;
    }
    setBusy(identifier, true);
    setError(null);
    // Optimistic update local.
    setLocalSecteurs((prev) => _patchLocal(prev, poi, flag, value));
    try {
      const body = {
        logement_id,
        section_type: "alentours",
        key: _findCategoryKey(localSecteurs, poi),
        flag,
        value,
      };
      // Inject identifiant selon source.
      if (poi.source === "google") body.place_id = poi.place_id;
      else if (poi.source === "tisseo") body.tisseo_url = poi.tisseo_url;
      else if (poi.source === "manual") body.manual_id = poi.manual_id;
      // Legacy fallback.
      else if (poi.place_id) body.place_id = poi.place_id;
      else if (poi.tisseo_url) body.tisseo_url = poi.tisseo_url;

      await _apiCall("livret_alentours_poi_toggle", { method: "POST", body });
      // Reload from server (re-sync flags + photo_reference enrichi P6).
      if (onReload) await onReload();
    } catch (e) {
      setError(e.message || "Erreur toggle");
      // Rollback local en cas d'échec.
      setLocalSecteurs(row?.content?.secteurs || {});
    } finally {
      setBusy(identifier, false);
    }
  }

  function handleToggleDisplay(poi, next) {
    return callToggle(poi, "displayed_to_guest", next);
  }
  function handleTogglePin(poi, next) {
    return callToggle(poi, "manual_pinned", next);
  }
  function handleToggleExclude(poi, next) {
    return callToggle(poi, "manual_excluded", next);
  }

  // ── Delete (manual POIs uniquement) ─────────────────────────────────────
  // Pessimist S1 PRÉCISION : passer manual_id (pas place_id) car protection
  // source=manual côté serveur.
  async function handleDelete(poi) {
    if (poi.source !== "manual") {
      setError("Seuls les POIs ajoutés manuellement peuvent être supprimés");
      return;
    }
    if (
      !window.confirm(
        `Supprimer définitivement "${poi.name}" ?\n\nL'action est réversible via un export JSON manuel (corbeille future).`,
      )
    ) {
      return;
    }
    const identifier = poi.manual_id;
    setBusy(identifier, true);
    setError(null);
    try {
      const key = _findCategoryKey(localSecteurs, poi);
      await _apiCall("livret_alentours_poi_delete_manual", {
        method: "POST",
        body: {
          logement_id,
          section_type: "alentours",
          key,
          manual_id_or_place_id: poi.manual_id, // S1 : strict manual_id
        },
      });
      if (onReload) await onReload();
    } catch (e) {
      setError(e.message || "Erreur suppression");
    } finally {
      setBusy(identifier, false);
    }
  }

  // ── Drag end : reorder intra-catégorie ──────────────────────────────────
  async function handleDragEnd(event, key) {
    isDraggingRef.current = false;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const arr = Array.isArray(localSecteurs[key]) ? localSecteurs[key] : [];
    const oldIndex = arr.findIndex((p) => getPoiIdentifier(p) === active.id);
    const newIndex = arr.findIndex((p) => getPoiIdentifier(p) === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(arr, oldIndex, newIndex);
    // Optimistic update local.
    setLocalSecteurs((prev) => ({ ...prev, [key]: reordered }));
    const ordered_place_ids = reordered.map(getPoiIdentifier).filter(Boolean);
    try {
      await _apiCall("livret_alentours_poi_reorder", {
        method: "POST",
        body: {
          logement_id,
          section_type: "alentours",
          key,
          ordered_place_ids,
        },
      });
      if (onReload) await onReload();
    } catch (e) {
      setError(e.message || "Erreur reorder");
      // Rollback.
      setLocalSecteurs(row?.content?.secteurs || {});
    }
  }

  // ── Régénération IA ─────────────────────────────────────────────────────
  async function handleRegenerate() {
    if (
      !window.confirm(
        "Régénérer le pool IA avec 15 nouvelles suggestions par catégorie ?\n\nLes choix manuels (épinglés, exclus, ajoutés manuellement) seront PRÉSERVÉS via merge automatique.",
      )
    ) {
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      await _apiCall("livret_sections_auto_generate", {
        method: "POST",
        body: { logement_id, type: "alentours" },
      });
      if (onReload) await onReload();
    } catch (e) {
      setError(e.message || "Erreur régénération");
    } finally {
      setGenerating(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: DS.fontBody }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h4
            style={{
              margin: 0,
              fontFamily: DS.fontDisplay,
              color: DS.navy,
              fontSize: 16,
            }}
          >
            🗺️ Édition Aux Alentours
          </h4>
          <p style={{ margin: "4px 0 0", fontSize: 11, color: "#666" }}>
            {activeCategoriesCount} catégories · {displayed} affichés / {total}{" "}
            au total
          </p>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={generating}
            style={_btnGhostStyle(DS.terra, generating)}
            title="Génère 15 nouvelles suggestions IA par catégorie. Préserve les choix manuels."
          >
            {generating ? "⏳ Régénération…" : "🔄 Régénérer IA"}
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" style={_errorBoxStyle()}>
          ❌ {error}
        </div>
      )}

      {/* 7 accordéons catégories */}
      {ALENTOURS_CATEGORIES.map((cat) => (
        <CategoryAccordion
          key={cat.key}
          cat={cat}
          pois={localSecteurs[cat.key] || []}
          sensors={sensors}
          busyKeys={busyKeys}
          isDraggingRef={isDraggingRef}
          onToggleDisplay={handleToggleDisplay}
          onTogglePin={handleTogglePin}
          onToggleExclude={handleToggleExclude}
          onDelete={handleDelete}
          onDragEnd={(ev) => handleDragEnd(ev, cat.key)}
          onAdd={() => setAddModalForKey(cat.key)}
        />
      ))}

      {/* Édition avancée JSON (collapse pour dev urgence) */}
      <details
        open={advancedOpen}
        onToggle={(e) => setAdvancedOpen(e.target.open)}
        style={{
          marginTop: 16,
          padding: 10,
          background: "#f8fafd",
          borderRadius: 8,
          border: `1px dashed ${DS.border}`,
        }}
      >
        <summary
          style={{
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
            color: "#666",
          }}
        >
          ⚙️ Édition avancée JSON (dev fallback)
        </summary>
        <pre
          style={{
            marginTop: 8,
            fontSize: 10,
            background: DS.white,
            padding: 8,
            borderRadius: 6,
            border: `1px solid ${DS.border}`,
            maxHeight: 300,
            overflow: "auto",
          }}
        >
          {JSON.stringify({ secteurs: localSecteurs }, null, 2)}
        </pre>
      </details>

      {/* Modal Ajouter POI manuellement */}
      {addModalForKey && (
        <AddPoiModal
          logement_id={logement_id}
          sectionKey={addModalForKey}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setAddModalForKey(null)}
          onAdded={async () => {
            setAddModalForKey(null);
            if (onReload) await onReload();
          }}
        />
      )}
    </div>
  );
}

// ── Sub-component : 1 accordéon par catégorie ─────────────────────────────
function CategoryAccordion({
  cat,
  pois,
  sensors,
  busyKeys,
  isDraggingRef,
  onToggleDisplay,
  onTogglePin,
  onToggleExclude,
  onDelete,
  onDragEnd,
  onAdd,
}) {
  const [open, setOpen] = useState(false);
  const count = pois.length;
  const displayedCount = pois.filter(
    (p) => p && p.displayed_to_guest !== false && p.manual_deleted !== true,
  ).length;
  const ids = pois.map(getPoiIdentifier).filter(Boolean);

  return (
    <section
      style={{
        marginBottom: 10,
        border: `1px solid ${DS.border}`,
        borderRadius: 10,
        background: DS.white,
      }}
    >
      <header
        style={{
          padding: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          // Règle #14 — en-tête OUVERT = marine plein (coins haut arrondis) ;
          // FERMÉ = blanc (hérité de la <section>).
          background: open ? DS.marine : "transparent",
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
        }}
      >
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={`accordion-${cat.key}-content`}
          style={{
            flex: 1,
            textAlign: "left",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: 4,
            minHeight: 40,
          }}
        >
          <span style={{ fontSize: 22 }} aria-hidden="true">
            {cat.icon}
          </span>
          <span
            style={{
              fontFamily: DS.fontDisplay,
              fontWeight: 700,
              color: open ? "#fff" : DS.navy,
              fontSize: 15,
            }}
          >
            {cat.label}
          </span>
          <span
            style={{
              fontSize: 11,
              color: open ? "rgba(255,255,255,.7)" : "#666",
              fontFamily: DS.fontBody,
            }}
          >
            ({displayedCount} affichés / {count} au total)
          </span>
          <span
            aria-hidden="true"
            style={{
              marginLeft: "auto",
              color: open ? "#fff" : DS.navy,
              fontSize: 18,
              transition: "transform 200ms ease",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            ▼
          </span>
        </button>
      </header>

      <div
        id={`accordion-${cat.key}-content`}
        style={{
          display: open ? "block" : "none",
          padding: open ? "0 10px 10px" : 0,
        }}
      >
        {count === 0 && (
          <p
            style={{
              fontSize: 12,
              color: "#888",
              fontStyle: "italic",
              margin: "8px 0",
            }}
          >
            Aucun POI dans cette catégorie. Cliquez sur "+ Ajouter manuellement"
            ou régénérez avec l'IA.
          </p>
        )}
        {count > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={() => {
              isDraggingRef.current = true;
            }}
            onDragEnd={onDragEnd}
            onDragCancel={() => {
              isDraggingRef.current = false;
            }}
          >
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              {pois.map((poi) => {
                const id = getPoiIdentifier(poi);
                if (!id) return null;
                return (
                  <AlentoursAdminCard
                    key={id}
                    poi={poi}
                    busy={!!busyKeys[id]}
                    onToggleDisplay={onToggleDisplay}
                    onTogglePin={onTogglePin}
                    onToggleExclude={onToggleExclude}
                    onDelete={onDelete}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        )}
        <button
          type="button"
          onClick={onAdd}
          style={{
            marginTop: 8,
            padding: "8px 12px",
            background: "transparent",
            color: DS.navy,
            border: `1px dashed ${DS.navy}`,
            borderRadius: 7,
            cursor: "pointer",
            fontFamily: DS.fontBody,
            fontSize: 12,
            fontWeight: 600,
            minHeight: 36,
          }}
        >
          ➕ Ajouter manuellement
        </button>
      </div>
    </section>
  );
}

// ── Helpers locaux ────────────────────────────────────────────────────────
//
// _findCategoryKey : retrouve la catégorie d'un POI dans l'objet secteurs
// (par recherche d'identifiant).
function _findCategoryKey(secteurs, poi) {
  const id = getPoiIdentifier(poi);
  if (!id || !secteurs || typeof secteurs !== "object") return null;
  for (const k of Object.keys(secteurs)) {
    const arr = Array.isArray(secteurs[k]) ? secteurs[k] : [];
    if (arr.some((p) => getPoiIdentifier(p) === id)) return k;
  }
  return null;
}

// _patchLocal : optimistic flag patch sur l'objet secteurs local.
function _patchLocal(secteurs, poi, flag, value) {
  const id = getPoiIdentifier(poi);
  if (!id) return secteurs;
  const next = { ...secteurs };
  for (const k of Object.keys(next)) {
    const arr = Array.isArray(next[k]) ? next[k] : [];
    const idx = arr.findIndex((p) => getPoiIdentifier(p) === id);
    if (idx >= 0) {
      const updated = { ...arr[idx], [flag]: value };
      // Cohérence : pin/excluded forcent displayed_to_guest.
      if (flag === "manual_pinned" && value === true) {
        updated.displayed_to_guest = true;
      }
      if (flag === "manual_excluded" && value === true) {
        updated.displayed_to_guest = false;
      }
      const arrCopy = arr.slice();
      arrCopy[idx] = updated;
      next[k] = arrCopy;
      break;
    }
  }
  return next;
}

function _btnGhostStyle(color, disabled) {
  return {
    padding: "8px 12px",
    background: DS.white,
    color,
    border: `1px solid ${color}`,
    borderRadius: 7,
    cursor: disabled ? "wait" : "pointer",
    fontFamily: DS.fontBody,
    fontSize: 12,
    fontWeight: 600,
    minHeight: 36,
    opacity: disabled ? 0.6 : 1,
  };
}

function _errorBoxStyle() {
  return {
    padding: 10,
    marginBottom: 12,
    background: "#fef2f2",
    border: `1px solid ${DS.charge}`,
    borderRadius: 7,
    color: DS.charge,
    fontSize: 12,
    fontFamily: DS.fontBody,
  };
}
